import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Banknote,
  TrendingUp,
  Wallet,
  LogOut,
  Menu,
  CreditCard,
  History,
  Coins,
} from "lucide-react";
import { authService } from "../services/api";
import { toPersianNumbers } from "../utils/numbers";

export default function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  // Close sidebar on route change for mobile
  React.useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  }, [location]);

  const isAdmin = user?.role === "admin";
  const isInvestor = user?.role === "investor";
  const hasPageAccess = (page: string) => {
    if (isAdmin) return true;
    if (isInvestor && page !== "users") return true;
    return Boolean(user?.permissions && user.permissions.includes(page));
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC] dark:bg-slate-900 font-sans text-slate-800 dark:text-slate-100">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 flex flex-col shadow-2xl lg:shadow-sm transition-all duration-300 lg:static overflow-hidden ${isSidebarOpen ? "translate-x-0 w-64" : "translate-x-full w-64 lg:translate-x-0 lg:w-0"}`}
      >
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3 shrink-0 h-[73px]">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <Menu className="w-6 h-6 shrink-0" />
          </button>
          <img
            src="/logo.png"
            alt="لوگوی نهام"
            className="w-8 h-auto shrink-0"
          />
          <span className="text-xl font-bold text-slate-900 dark:text-white whitespace-nowrap">
            سامانه نهام
          </span>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* 1. خانه */}
          {hasPageAccess("dashboard") && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>خانه</span>
            </NavLink>
          )}

          {/* 2. سرمایه گذاری */}
          {hasPageAccess("investments") && (
            <NavLink
              to="/investments"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <TrendingUp className="w-5 h-5" />
              <span>سرمایه گذاری</span>
            </NavLink>
          )}

          {/* 3. جریمه‌های هفتگی */}
          {(hasPageAccess("weekly-penalties") ||
            hasPageAccess("investments")) && (
            <NavLink
              to="/weekly-penalties"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Coins className="w-5 h-5 text-amber-500" />
              <span>جریمه‌های هفتگی</span>
            </NavLink>
          )}

          {/* 4. موجودی صندوق */}
          {hasPageAccess("balance") && (
            <NavLink
              to="/balance"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Wallet className="w-5 h-5" />
              <span>موجودی صندوق</span>
            </NavLink>
          )}

          {/* 5. تایید اقساط پرداختی */}
          {hasPageAccess("installments") && (
            <NavLink
              to="/installments"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <CreditCard className="w-5 h-5" />
              <span>تایید اقساط پرداختی</span>
            </NavLink>
          )}

          {/* 6. ثبت نام اولیه وام */}
          {hasPageAccess("loans") && (
            <NavLink
              to="/loans"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Banknote className="w-5 h-5" />
              <span>ثبت نام اولیه وام</span>
            </NavLink>
          )}

          {/* 7. تایید ثبت اولیه */}
          {hasPageAccess("loan-reservations") && (
            <NavLink
              to="/loan-reservations"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>تایید ثبت اولیه</span>
            </NavLink>
          )}

          {/* 8. تایید کارمزد ثبت نام اولیه وام */}
          {(hasPageAccess("loan-fee-receipt") ||
            hasPageAccess("loan-reservations")) && (
            <NavLink
              to="/loan-fee-receipt"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Banknote className="w-5 h-5" />
              <span>تایید کارمزد ثبت نام اولیه وام</span>
            </NavLink>
          )}

          {/* 9. پرداخت وام */}
          {hasPageAccess("loan-payments") && (
            <NavLink
              to="/loan-payments"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <CreditCard className="w-5 h-5" />
              <span>پرداخت وام</span>
            </NavLink>
          )}

          {/* 10. تاریخچه وام */}
          {hasPageAccess("loan-history") && (
            <NavLink
              to="/loan-history"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <History className="w-5 h-5" />
              <span>تاریخچه وام</span>
            </NavLink>
          )}

          {/* 11. هزینه ها */}
          {hasPageAccess("expenses") && (
            <NavLink
              to="/expenses"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Banknote className="w-5 h-5" />
              <span>هزینه ها</span>
            </NavLink>
          )}

          {/* 12. مدیریت کاربران (فقط ادمین) */}
          {(isAdmin ||
            (user?.permissions && user.permissions.includes("users"))) && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "bg-persian-50 text-persian-600 dark:bg-persian-900/30 dark:text-persian-400 font-semibold"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-slate-200"
                }`
              }
            >
              <Users className="w-5 h-5" />
              <span>مدیریت کاربران</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-persian-100 dark:bg-persian-900/50 flex items-center justify-center text-persian-600 dark:text-persian-400 font-bold shrink-0">
              {user?.first_name?.[0] || "U"}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                {user?.first_name} {user?.last_name}
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {toPersianNumbers(user?.national_code)}
                </span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium ${
                    user?.role === "admin"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
                      : user?.role === "investor"
                        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                  }`}
                >
                  {user?.role === "admin"
                    ? "مدیر"
                    : user?.role === "investor"
                      ? "سرمایه‌گذار (فقط مشاهده)"
                      : "عضو"}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span>خروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden min-w-0">
        {/* Mobile Header */}
        <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between shadow-sm shrink-0 h-[73px]">
          <div className="flex items-center gap-3">
            {!isSidebarOpen && (
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 p-2 rounded-lg transition-colors"
              >
                <Menu className="w-6 h-6 shrink-0" />
              </button>
            )}
            <div
              className={`flex items-center gap-3 ${isSidebarOpen ? "lg:hidden" : ""}`}
            >
              <img
                src="/logo.png"
                alt="لوگوی نهام"
                className="w-8 h-auto shrink-0"
              />
              <span className="text-lg font-bold text-slate-900 dark:text-white whitespace-nowrap">
                سامانه نهام
              </span>
            </div>
          </div>

          {/* User profile or other header actions can go here */}
          <div></div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-8">
          <div className="max-w-6xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
